/**
 * Validación de archivos de la biblioteca DOOH (TG-58) — espejo de las reglas del
 * backend (`dooh-media-format.ts` y `DoohMediaService.upload`):
 *
 * - Solo JPG (`image/jpeg`, `.jpg`/`.jpeg`) y MP4 (`video/mp4`, `.mp4`).
 * - El MIME **y** la extensión deben coincidir (rechaza archivos mal etiquetados).
 * - Tamaño máximo: 10 MB imagen / 100 MB video (defaults del backend; se pueden
 *   ajustar con `DOOH_MAX_IMAGE_BYTES` / `DOOH_MAX_VIDEO_BYTES`).
 *
 * Validar en cliente da feedback inmediato; el backend vuelve a validar (fuente de
 * verdad). No hay secretos ni endpoints hardcodeados.
 */
import type { DoohMediaKind } from '@goelectric/shared-types';

/** Tope de tamaño para imágenes (default del backend: `DOOH_MAX_IMAGE_BYTES`). */
export const DOOH_MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/** Tope de tamaño para videos (default del backend: `DOOH_MAX_VIDEO_BYTES`). */
export const DOOH_MAX_VIDEO_BYTES = 100 * 1024 * 1024;

/** `accept` del selector de archivos (extensión + MIME). */
export const DOOH_ACCEPT = '.jpg,.jpeg,.mp4,image/jpeg,video/mp4';

/** Extensiones y MIME admitidos, por tipo de medio. */
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg'];
const VIDEO_EXTENSIONS = ['.mp4'];
const IMAGE_MIME = 'image/jpeg';
const VIDEO_MIME = 'video/mp4';

export type DoohFileValidation =
  | { valid: true; kind: DoohMediaKind }
  | { valid: false; error: string };

/** Extensión en minúsculas del nombre (con punto), o cadena vacía. */
export function fileExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot).toLowerCase() : '';
}

/** Formatea bytes a MB con un decimal (solo para mensajes de error). */
function toMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Valida un archivo contra el contrato. Devuelve el `kind` cuando es aceptable o
 * un mensaje en español cuando no lo es.
 */
export function validateDoohFile(file: Pick<File, 'name' | 'type' | 'size'>): DoohFileValidation {
  const extension = fileExtension(file.name);
  const mime = (file.type || '').toLowerCase();

  const isImage = IMAGE_EXTENSIONS.includes(extension) && mime === IMAGE_MIME;
  const isVideo = VIDEO_EXTENSIONS.includes(extension) && mime === VIDEO_MIME;

  if (!isImage && !isVideo) {
    return {
      valid: false,
      error: 'Formato no permitido: solo se aceptan imágenes JPG o videos MP4.',
    };
  }
  if (file.size <= 0) {
    return { valid: false, error: 'El archivo está vacío.' };
  }

  const kind: DoohMediaKind = isVideo ? 'VIDEO' : 'IMAGE';
  const maxBytes = isVideo ? DOOH_MAX_VIDEO_BYTES : DOOH_MAX_IMAGE_BYTES;
  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `El archivo supera el máximo permitido para ${isVideo ? 'video' : 'imagen'} (${toMb(maxBytes)}).`,
    };
  }
  return { valid: true, kind };
}
